/**
 * audioFocus — a neutral, app-level audio-focus arbiter shared by the two
 * independent audio subsystems (the Audio Lessons player and the chat TTS),
 * which own SEPARATE engines + OS audio sessions and must never play at once.
 *
 * This is the "starting one pauses the other" coordination the Audio Lessons
 * BOUNDARY calls for. It is deliberately a thin arbiter (à la Android
 * `AudioManager.requestAudioFocus`), NOT shared playback state: it knows only
 * *which* subsystem currently holds focus and *how* the previous holder yields.
 * Neither subsystem imports the other, neither routes audio through the other,
 * and no chat-TTS / lessons concepts leak across — see
 * docs/specs/mobile/audio-lessons.mobile.md "BOUNDARY".
 *
 * (expo-audio doesn't surface AVAudioSession interruptions to JS, and chat TTS
 * runs on a separate Azure/expo-speech session, so pure OS-interruption
 * enforcement isn't reliably observable from JS — this arbiter makes the
 * hand-off deterministic. Final behavior still needs on-device validation.)
 */

export type AudioFocusOwner = 'audio-lessons' | 'chat-tts';

let holder: AudioFocusOwner | null = null;
const yielders = new Map<AudioFocusOwner, () => void>();

/**
 * Claim exclusive audio focus for `owner`. If a DIFFERENT owner currently holds
 * it, that owner's registered yield handler runs (pause/stop). Pass `onYield` to
 * (re)register how THIS owner yields when something else later takes focus.
 */
export function takeAudioFocus(owner: AudioFocusOwner, onYield?: () => void): void {
  if (onYield) yielders.set(owner, onYield);
  if (holder && holder !== owner) {
    try {
      yielders.get(holder)?.();
    } catch {
      /* a yielder must never block the new owner from taking focus */
    }
  }
  holder = owner;
}

/** Release focus if `owner` currently holds it (no-op otherwise). */
export function releaseAudioFocus(owner: AudioFocusOwner): void {
  if (holder === owner) holder = null;
}

/** The current focus holder (null = idle). Exposed for tests/diagnostics. */
export function getAudioFocusHolder(): AudioFocusOwner | null {
  return holder;
}
